import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import UsersTable from '@/components/users/UsersTable';
import { Button } from '@/components/ui/button';
import {
  SimpleModal,
  SimpleModalHeader,
  SimpleModalTitle,
  SimpleModalDescription,
  SimpleModalContent,
  SimpleModalFooter,
} from "@/components/ui/simple-modal";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthContext } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Users2, UserPlus, Shield, UserCheck, UserX, UserCog, TrendingUp, Activity } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { LoadingScreen } from '@/components/common';
import { generateStrongPassword } from '@/utils/passwordGenerator';

// Define the Role interface
interface Role {
  id: string;
  name: string;
  description: string;
  is_system: boolean;
}

interface UserSummary {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  rolesCount: number;
}

export default function Users() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolesLoading, setRolesLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<UserSummary>({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    rolesCount: 0
  });
  const { toast } = useToast();
  const { user } = useAuthContext();

  // Fetch user summary data
  const fetchUsersSummary = async () => {
    try {
      // Get count of all users
      const { count: totalCount, error: totalError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      if (totalError) throw totalError;

      // Get count of active users
      const { count: activeCount, error: activeError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (activeError) throw activeError;

      // Get count of inactive users
      const { count: inactiveCount, error: inactiveError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', false);

      if (inactiveError) throw inactiveError;

      // Get count of roles
      const { count: rolesCount, error: rolesCountError } = await supabase
        .from('roles')
        .select('*', { count: 'exact', head: true });

      if (rolesCountError) throw rolesCountError;

      setSummaryData({
        totalUsers: totalCount || 0,
        activeUsers: activeCount || 0,
        inactiveUsers: inactiveCount || 0,
        rolesCount: rolesCount || 0
      });
    } catch (error) {
      console.error('Error fetching users summary:', error);
    }
  };

  // Fetch available roles
  useEffect(() => {
    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const { data, error } = await supabase
          .from('roles')
          .select('*')
          .order('name');
        
        if (error) throw error;
        
        setRoles(data || []);
        
        // Set default role (User) if available
        const userRole = data?.find(role => role.name === 'User');
        if (userRole) {
          setSelectedRoleId(userRole.id);
        } else if (data && data.length > 0) {
          setSelectedRoleId(data[0].id);
        }
      } catch (error: any) {
        console.error('Error fetching roles:', error);
        toast({
          variant: "destructive",
          title: "Erro ao carregar papéis",
          description: "Não foi possível carregar a lista de papéis disponíveis.",
        });
      } finally {
        setRolesLoading(false);
      }
    };
    
    const initializeData = async () => {
      await Promise.all([
        fetchRoles(),
        fetchUsersSummary()
      ]);
      setInitialLoading(false);
    };
    
    initializeData();
  }, [toast, refreshTrigger]);

  // Cleanup effect to reset modal states
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      setIsModalOpen(false);
      setIsClosing(false);
      setLoading(false);
    };
  }, []);

  // Função para forçar a atualização da tabela
  const refreshTable = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    fetchUsersSummary(); // Update summary when refreshing
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🚀 Modal submit started - loading:', loading, 'isClosing:', isClosing, 'modalOpen:', isModalOpen);
    
    if (loading || isClosing || !isModalOpen) return;
    
    // Validations
    if (!selectedRoleId) {
      toast({
        variant: "destructive",
        title: "Papel não selecionado",
        description: "Por favor, selecione um papel para o usuário.",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Generate a strong random password for the user
      const generatedPassword = generateStrongPassword();
      
      // Criar usuário usando Edge Function (resolve problema de permissões)
      const { data: responseData, error: functionError } = await supabase.functions.invoke('create-user', {
        body: {
          email,
          password: generatedPassword,
          firstName: firstName || null,
          lastName: lastName || null,
          roleId: selectedRoleId,
          createdBy: user?.id
        }
      });

      if (functionError) {
        console.error('Function error:', functionError);
        throw new Error(functionError.message || 'Erro ao chamar função de criação de usuário');
      }

      if (!responseData?.success) {
        throw new Error(responseData?.error || 'Erro desconhecido na criação do usuário');
      }

      // Verificar se houve warning (usuário criado mas papel não atribuído)
      if (responseData.warning) {
        toast({
          variant: "default",
          title: "Usuário criado com ressalva",
          description: responseData.warning,
        });
      } else {
        toast({
          title: "Usuário criado com sucesso",
          description: responseData.message || "O usuário foi criado e receberá um email de confirmação para acessar o sistema.",
        });
      }

      // Mark as closing before cleanup
      console.log('✅ User created successfully, closing modal...');
      setIsClosing(true);
      
      // Close modal with delay to ensure smooth transition
      setTimeout(() => {
        console.log('🔄 Closing modal and resetting form...');
        setIsModalOpen(false);
        resetForm();
        refreshTable(); // Atualiza a tabela após criar o usuário
        // Ensure isClosing is reset after all operations
        setIsClosing(false);
        console.log('✨ Modal closed and states reset');
      }, 100);
      
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Melhor tratamento de erros específicos
      let errorMessage = "Não foi possível criar o usuário. Por favor, tente novamente.";
      
      if (error.message?.includes("User already registered")) {
        errorMessage = "Este e-mail já está registrado no sistema.";
      }
      
      toast({
        variant: "destructive",
        title: "Erro ao criar usuário",
        description: errorMessage,
      });
      
      // Reset all states on error
      console.log('❌ Error occurred, resetting states...');
      setLoading(false);
      setIsClosing(false);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setLoading(false);
    setIsClosing(false);
    // Reset to default role (User) if available
    const userRole = roles.find(role => role.name === 'User');
    if (userRole) {
      setSelectedRoleId(userRole.id);
    } else if (roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    } else {
      setSelectedRoleId('');
    }
  };

  const handleCloseModal = () => {
    console.log('🚪 Closing modal manually - loading:', loading, 'isClosing:', isClosing);
    if (!loading && !isClosing) {
      setIsClosing(true);
      resetForm();
      setIsModalOpen(false);
      // Ensure isClosing is reset after modal closes
      setTimeout(() => {
        setIsClosing(false);
        console.log('🔄 Manual close completed');
      }, 50);
    }
  };

  const handleOpenModal = () => {
    console.log('🚪 Opening modal...');
    // Ensure clean state before opening
    setIsClosing(false);
    setLoading(false);
    setIsModalOpen(true);
  };

  if (initialLoading) {
    return <LoadingScreen message="Carregando Usuários..." size="x-small" />;
  }

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Usuários</title>
      </Helmet>
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Modern Gradient */}
        <div className="relative bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
          
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Users2 className="h-8 w-8" />
                  </div>
                  <div className="h-8 w-px bg-white/30" />
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-white/80" />
                    <span className="text-white/80">Administração</span>
                  </div>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                  Usuários
                </h1>
                
                <p className="text-xl text-white/90 max-w-2xl">
                  Controle total sobre usuários, permissões e níveis de acesso da sua organização
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleOpenModal} 
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Adicionar Usuário
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Enhanced Summary Cards with Better Visual Hierarchy */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <Users2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Usuários</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalUsers}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Em {summaryData.rolesCount} papéis diferentes
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-blue-500" />
                      <span className="text-xs text-green-600 font-extrabold">+8%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <UserCheck className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Usuários Ativos</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.activeUsers}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Progress 
                      value={summaryData.totalUsers > 0 ? (summaryData.activeUsers / summaryData.totalUsers) * 100 : 0} 
                      className="h-1.5"
                    />
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {summaryData.totalUsers > 0 
                          ? `${Math.round((summaryData.activeUsers / summaryData.totalUsers) * 100)}% do total` 
                          : 'Nenhum usuário'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-green-600 font-extrabold">+3%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md">
                        <UserX className="h-4 w-4 text-yellow-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Usuários Inativos</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.inactiveUsers}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Progress 
                      value={summaryData.totalUsers > 0 ? (summaryData.inactiveUsers / summaryData.totalUsers) * 100 : 0} 
                      className="h-1.5"
                    />
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {summaryData.totalUsers > 0 
                          ? `${Math.round((summaryData.inactiveUsers / summaryData.totalUsers) * 100)}% do total` 
                          : 'Nenhum usuário'}
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3 text-yellow-500 rotate-180" />
                        <span className="text-xs text-yellow-600 font-extrabold">-1%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <UserCog className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Papéis</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.rolesCount}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Diferentes níveis de acesso
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3 text-purple-500" />
                      <span className="text-xs text-purple-600 font-extrabold">Seguros</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Main Content with Modern Design */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">Lista de Usuários</CardTitle>
                        <Shield className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <CardDescription className="text-base">
                        Visualize e gerencie todos os usuários cadastrados no sistema com seus respectivos papéis e permissões
                      </CardDescription>
                    </div>
                  </div>
                  
                  <div className="overflow-hidden">
                    <UsersTable 
                      refreshTrigger={refreshTrigger}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Bottom Spacing */}
          <div className="h-16" />
        </div>

      {/* Enhanced Modal */}
      <SimpleModal 
        open={isModalOpen} 
        onOpenChange={(open) => {
          console.log('🔄 SimpleModal onOpenChange called with:', open);
          if (!open && !loading && !isClosing) {
            handleCloseModal();
          }
        }}
        className="sm:max-w-[700px] w-full mx-4"
      >
        <SimpleModalHeader className="space-y-4 pb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100">
              <UserPlus className="h-6 w-6 text-blue-600" />
            </div>
            <div className="space-y-1">
              <SimpleModalTitle className="text-2xl font-extrabold text-gray-900">Adicionar Novo Usuário</SimpleModalTitle>
              <SimpleModalDescription className="text-base text-gray-600">
                Configure as informações e permissões do novo usuário no sistema
              </SimpleModalDescription>
            </div>
          </div>
        </SimpleModalHeader>

        <SimpleModalContent>

          <form id="user-form" onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information Section */}
            <div className="space-y-6">
              <div className="space-y-2">
                <h4 className="text-lg font-extrabold text-gray-900">Informações Pessoais</h4>
                <p className="text-sm text-gray-600">Configure os dados básicos do usuário</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="firstName" className="text-sm font-extrabold text-gray-700">
                    Nome
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Nome do usuário"
                    className="h-12 text-base bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    disabled={loading || isClosing}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="lastName" className="text-sm font-extrabold text-gray-700">
                    Sobrenome
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Sobrenome do usuário"
                    className="h-12 text-base bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    disabled={loading || isClosing}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="email" className="text-sm font-extrabold text-gray-700">
                  Endereço de Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="usuario@exemplo.com"
                  className="h-12 text-base bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  required
                  disabled={loading || isClosing}
                />
                
              </div>
            </div>



            <Separator />
            
            {/* Role Selection Section */}
            <div className="space-y-6">
              <div className="space-y-2">
                <h4 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-purple-600" />
                  Papel e Permissões
                </h4>
                <p className="text-sm text-gray-600">Defina o nível de acesso do usuário no sistema</p>
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="role" className="text-sm font-extrabold text-gray-700">
                  Papel do Usuário *
                </Label>
                <Select
                  value={selectedRoleId}
                  onValueChange={setSelectedRoleId}
                  disabled={rolesLoading || loading || isClosing}
                >
                  <SelectTrigger id="role" className="h-12 text-base bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue placeholder="Selecione um papel para o usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id} className="py-4">
                        <div className="flex flex-col items-start space-y-1">
                          <span className="font-extrabold text-gray-900">{role.name}</span>
                          {role.description && (
                            <span className="text-sm text-gray-500">{role.description}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rolesLoading && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded-md">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Carregando papéis disponíveis...
                  </div>
                )}
              </div>
            </div>
            
          </form>
        </SimpleModalContent>

        <SimpleModalFooter className="flex flex-col-reverse sm:flex-row gap-3 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleCloseModal}
            disabled={loading || isClosing}
            className="h-12 px-8 text-base border-gray-300 hover:bg-gray-50 transition-all duration-200"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            form="user-form"
            disabled={loading || isClosing || rolesLoading || !email || !selectedRoleId}
            className="h-12 px-12 text-base bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                Criando usuário...
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5 mr-3" />
                Criar Usuário
              </>
            )}
          </Button>
        </SimpleModalFooter>
      </SimpleModal>
    </>
  );
} 